import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Bienvenue !");
            nav("/");
        } catch (err) {
            // iter56 — Prefer the friendly hint set by the axios interceptor
            // for edge (Cloudflare) 403/520-524 errors so the toast never
            // shows the raw "Request failed with status code X" fallback.
            const friendly = err?._friendly;
            const detail = err?.response?.data?.detail;
            toast.error(friendly || detail || "Echec de connexion");
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-discover-soft">
            <div className="w-full max-w-md bg-card rounded-3xl shadow-2xl p-8 space-y-6">
                <div className="flex justify-center"><Logo size={56} /></div>
                <div className="text-center space-y-1">
                    <h1 className="text-3xl font-black discover-gradient-text">Bon retour !</h1>
                    <p className="text-muted-foreground">Connecte-toi pour découvrir.</p>
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                    <div>
                        <label className="text-xs font-semibold mb-1 block">Email</label>
                        <Input type="email" required value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="rounded-2xl" data-testid="login-email" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold mb-1 block">Mot de passe</label>
                        <div className="relative">
                            <Input type={show ? "text" : "password"} required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="rounded-2xl pr-10" data-testid="login-password" />
                            <button type="button" onClick={() => setShow(!show)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                                data-testid="login-show-pwd">
                                {show ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>
                    <Button type="submit" disabled={loading}
                        className="w-full rounded-full bg-discover-gradient text-white"
                        data-testid="login-submit">
                        {loading ? "Connexion..." : "Se connecter"}
                    </Button>
                </form>

                {/* Social login. REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY
                    FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-[10px] uppercase font-bold text-muted-foreground">ou</span>
                    <div className="h-px flex-1 bg-border" />
                </div>
                <button
                    type="button"
                    onClick={() => {
                        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY
                        // FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                        const redirectUrl = window.location.origin + "/";
                        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    data-testid="login-google-btn"
                    className="w-full rounded-full border-2 border-border bg-card hover:bg-accent transition flex items-center justify-center gap-2 py-2.5 font-semibold text-sm">
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09A6.74 6.74 0 0 1 5.5 12c0-.74.13-1.46.34-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                    </svg>
                    Continuer avec Google
                </button>
                <div className="text-center text-xs space-y-2">
                    <Link to="/forgot-password" className="text-primary font-semibold" data-testid="login-forgot-link">
                        Mot de passe oublie ?
                    </Link>
                    <p className="text-muted-foreground">
                        Pas de compte ? <Link to="/register" className="text-primary font-semibold" data-testid="login-register-link">S'inscrire</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
